import { useCallback, useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import { setDialogOpen } from 'common/components/deleteDialog/deleteDialogSlice';
import { setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteUserPackageAssignment } from 'common/services/api/ProductPackages/PackageAssignments.thunk';
import { DeleteDialogContentProps } from 'common/types/TypeDefs';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export interface UserProductPackageDeleteDialogProperties {
    tenantId: string;
    tenantName: string;
    tenantRequiresLicense: boolean;
}

type PPARow = {
    packageName: string;
    productPackageId: string;
    licenseId?: string;
    userId?: string;
    principalName: string;
    billingRecord?: string;
};

export function GetUserProductPackageDeleteDialogContent(dialogProps: UserProductPackageDeleteDialogProperties) {
    return UserProductPackageDeleteDialogContent.bind(null, dialogProps);
}

export function UserProductPackageDeleteDialogContent(
    dialogProps: UserProductPackageDeleteDialogProperties,
    itemProps: DeleteDialogContentProps<PPARow>
) {
    const { tenantId, tenantName, tenantRequiresLicense } = dialogProps;
    const { itemId, editingRow } = itemProps;
    const { packageName, productPackageId, licenseId, userId, principalName, billingRecord } = editingRow ?? {};

    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const deleteCallback = useCallback(() => {
        if (!itemId) return;

        setProcessing(true);
        dispatch(deleteUserPackageAssignment({
            tenantId,
            packageId: productPackageId ?? '',
            userId: userId ?? '',
            licenseId: licenseId ?? '',
            withLicenseInfo: tenantRequiresLicense ?? false,
        }))
            .unwrap()
            .then((rows) => {
                dispatch(setRows(rows));
                dispatch(setDialogOpen(false));
                enqueueSnackbar('Product Package assignment has been deleted.', { variant: 'success' });
            })
            .catch((error) => {
                handleDeleteDialogError('Product Package assignment', enqueueSnackbar, extractStatusCode(error.message));
            })
            .finally(() => setProcessing(false));
    }, [itemId, productPackageId, licenseId, tenantId]);

    return (
        <DeleteDialog deleteCallback={deleteCallback} processing={processing} title='Product Package Assignment'>
            <DeleteDialogCustomField fieldName="Tenant Name" fieldValue={tenantName} />
            <DeleteDialogCustomField fieldName="Package Name" fieldValue={packageName} />
            <DeleteDialogCustomField fieldName="Principal Name" fieldValue={principalName} />
            {billingRecord && <DeleteDialogCustomField fieldName="Billing Record" fieldValue={billingRecord} /> }
        </DeleteDialog>

    );
}
